import React, { useState, useMemo } from 'react';

import {
  trackDemoEnterEmailSuccess,
  trackDemoEnterEmailError,
} from '../../../analytics';

import { Form, FloatingLabel } from '../../../components/form';
import { Button } from '../../../components/general';
import { ContentHeader } from '../../../components/layout/content';

import useDemoData from '../../../hooks/useDemoData';
import { useWindowWidth } from '../../../hooks/useWindowWidth';

import { checkInPerson } from '../../../services/checkInService';
import { sendSlackDemoNotification } from '../../../services/slackService';

import { PublicEvent, Face, ModalError } from '../../../types';
import { validateEmail, getBpWidth } from '../../../utils';

interface Props {
  event: PublicEvent;
  nextStep: () => void;
  handleExistingUser: (face: Face) => Promise<void>;
  setError: React.Dispatch<React.SetStateAction<ModalError>>;
}

const IntroStep: React.FC<Props> = (props) => {
  const { event, nextStep, handleExistingUser, setError } = props;

  const { updateDemoData } = useDemoData();

  const windowWidth = useWindowWidth();
  const mobilePrefix = useMemo(() => {
    return windowWidth < getBpWidth('sm') ? 'mobile_' : '';
  }, [windowWidth]);

  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [emailErr, setEmailErr] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailErr('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsProcessing(true);

    const emailUpd = email.toLowerCase().trim();

    const { emailErr } = validateEmail(emailUpd);

    if (emailErr) {
      setEmailErr(emailErr);
      setIsProcessing(false);
      return;
    }

    try {
      const { face, isNew } = await checkInPerson({
        event,
        personData: { email: emailUpd, name: emailUpd.split('@')[0] },
        noEmail: true,
        trackOnSuccess: trackDemoEnterEmailSuccess,
        trackOnError: trackDemoEnterEmailError,
      });

      await sendSlackDemoNotification({
        action: 'Entered Email',
        user: face,
        details: isNew ? 'New user' : 'Existing user',
      });

      if (isNew) {
        updateDemoData({
          status: 'upload',
          user: { email: face.email, elementId: face.elementId },
          photos: [],
          clips: [],
          notifications: { analysis: true, generation: true },
        });

        nextStep();
      } else await handleExistingUser(face);
    } catch (err: any) {
      await sendSlackDemoNotification({
        action: 'Entered Email',
        user: { email: emailUpd },
        details: err.message,
        isError: true,
      });
      setError({ subheading: 'Please try again later.' });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <React.Fragment>
      <ContentHeader title="Welcome to CrowdClip&reg; demo" />

      <div className="text-lg">
        See how CrowdClip&reg; personalises videos for <b>each attendee</b> — in
        3 quick steps.
        <br />
        Upload a selfie → Upload two short videos → Get your personalised
        highlight reel.
      </div>

      <Form
        onSubmit={handleSubmit}
        formClassName="w-full sm:w-3/4"
        className="flex-col sm:flex-row gap-2 sm:gap-4 text-left"
      >
        <FloatingLabel
          label="Enter email to get started"
          name="email"
          value={email}
          onChange={handleChange}
          isInvalid={!!emailErr}
          errMsg={emailErr}
          required
          className="w-full sm:w-3/4"
        />

        <Button
          className="w-full sm:w-1/4"
          isSubmitBtn
          isProcessing={isProcessing}
        >
          Let's Go!
        </Button>
      </Form>

      <video
        src={`https://crowdclip-media-assets.s3.ap-southeast-2.amazonaws.com/${mobilePrefix}one-to-many-video.mp4`}
        autoPlay
        muted
        loop
        playsInline
        className="w-full sm:w-3/4 aspect-video -my-2 rounded-md object-cover"
      />

      <div>
        In this demo, you’ll see how CrowdClip&reg; uses AI to find you in the
        footage and generate a personalised highlight reel — automatically.
      </div>

      <div className="italic">
        The workflow is simplified. The output is real.
      </div>
    </React.Fragment>
  );
};

export default IntroStep;
